import React from 'react';
import { flowRight } from 'lodash/fp';
import { withSnackbar } from 'notistack';

import StepsForm from 'ui/form/StepsForm';

import CompanyEditStep from './steps/CompanyEditStep';
import LoaderStep from './steps/LoaderStep';
import { loadCompany } from './services';
import { modifyCompany } from '../../Services';
import * as valid from 'utils/validators';
import { withAppContext } from '../../AppContext';
import { MAX_SMALL_INTEGER, NAME_MAX_LENGTH } from './consts';

const EDIT_STEP = 'account';
const LOADER_STEP = 'loader';

const INITIAL_VALUES = {
    business_name: '',
    address1: '',
    address2: '',
    city: '',
    country: '',
    region: '',
    postal_code: '',
    name: '',
    logo: '',
    type: '',
    subscription_level: 'advanced',
    seats: '',
    users: 0,
};

const wasChangeInLicense = (_, changes) =>
    changes.includes('seats') || changes.includes('subscription_level');

const STEPS = {
    [LOADER_STEP]: {
        component: LoaderStep,
        update: async ({ goNext, setInitValues, extra }) => {
            const data = await loadCompany(extra.params.id);
            setInitValues({ ...data });
            goNext();
        },
        next: EDIT_STEP,
    },
    [EDIT_STEP]: {
        component: CompanyEditStep,
        validate: valid.rules([
            valid.notEmpty('business_name'),
            valid.maxLength('business_name', NAME_MAX_LENGTH),
            valid.notEmpty('name'),
            valid.maxLength('name', NAME_MAX_LENGTH),
            valid.notEmpty('subscription_level'),
            valid.maxAllowedValue('seats', MAX_SMALL_INTEGER),
            valid.notEmpty('seats'),
            valid.minAllowedSeats(),
            valid.conditionCheck(
                wasChangeInLicense,
                valid.rules([valid.notEmpty('licenseAck')])
            ),
            valid.conditionCheck(
                values => values.type !== 'customer' && !values.business_name,
                valid.rules([
                    valid.notEmpty('address1'),
                    valid.notEmpty('city'),
                    valid.notEmpty('country'),
                    valid.notEmpty('region'),
                    valid.notEmpty('postal_code'),
                ])
            ),
        ]),
        next: async ({
            onSave,
            onError,
            onReset,
            values,
            changes,
            extra,
            setInitValues,
        }) => {
            if (!changes.length) {
                return true;
            }
            const uploadLogo = changes.includes('logo');
            const isLicense =
                changes.includes('seats') ||
                changes.includes('subscription_level');
            const result = await onSave(
                extra.params.id,
                values,
                uploadLogo,
                isLicense
            );
            if (result && result.error) {
                onError(prepareErrorResult(result));
                return false;
            }
            onReset();
            setInitValues({ ...values });
            return true;
        },
    },
};

const prepareErrorResult = result =>
    Object.keys(result.details).reduce((buff, key) => {
        buff[key] = {
            serverSide: true,
            message: result.details[key],
        };
        return buff;
    }, {});

const handleSave = (app, notify) => async (
    uid,
    data,
    uploadLogo,
    isLicense
) => {
    let update = 'UPDATE_COMPANY';
    if (isLicense) update = 'UPDATE_LICENSE';
    let notification = 'Organization updated';
    if (uploadLogo) notification = 'Organization logo updated';
    const results = await modifyCompany(
        app.currentUser.uuid,
        update,
        uid,
        data,
        uploadLogo
    );
    if (results) {
        return results;
    }
    notify(notification, {
        variant: 'info',
    });
};

function CompanyEdit({ enqueueSnackbar, app, id, editable }) {
    const extra = {
        user: app.currentUser,
        params: { id },
        editable,
    };
    return (
        <StepsForm
            values={INITIAL_VALUES}
            steps={STEPS}
            first={LOADER_STEP}
            onSave={handleSave(app, enqueueSnackbar)}
            extra={extra}
        />
    );
}

export default flowRight(withAppContext, withSnackbar)(CompanyEdit);
